// Types for the GPO restaurant-inquiries API (ums/v1/cms/restaurant-inquiries).

export enum PaymentOption {
  CASH = 'CASH',
  // Legacy generic credit — still returned for pre-existing inquiries; the
  // creation UI offers the two specific credit types below instead.
  CREDIT = 'CREDIT',
  BILL_TO_BILL = 'BILL_TO_BILL',
  // End of Month credit — creditDays counts from month end.
  CREDIT_EOM = 'CREDIT_EOM',
  // Credit with creditDays counted from the delivery date.
  CREDIT_DELIVERY = 'CREDIT_DELIVERY',
}

// Backend enum. A freshly submitted inquiry starts as NEW; DRAFT is
// save-without-submit. FULFILLED = Closed Won, UNFULFILLED = Closed Lost.
export enum RestaurantInquiryStatus {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  PROCESSING = 'PROCESSING',
  QUOTATION_READY = 'QUOTATION_READY',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED',
  UNFULFILLED = 'UNFULFILLED',
  CANCELLED = 'CANCELLED',
}

// Per-item status within an inquiry.
export enum RestaurantInquiryItemStatus {
  CREATED = 'CREATED',
  PROCESSING = 'PROCESSING',
  QUOTED = 'QUOTED',
  FULFILLED = 'FULFILLED',
  UNFULFILLED = 'UNFULFILLED',
}

export enum VolumePeriod {
  PER_ORDER = 'PER_ORDER', // backend default
  ONE_TIME = 'ONE_TIME',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  ANNUAL = 'ANNUAL',
}

// Exactly 3 chars; backend defaults to AED.
export type CurrencyCode = string;

// One acceptable pack format — the packaging subset of a variant MINUS
// baseUnit, which is never flexible (it is the denominator volume and every
// quote price are expressed in). An omitted field is "unspecified" and never
// disqualifies a candidate, the same rule the string dimensions follow.
export interface PackagingSpec {
  itemUnit?: string | null;
  netContent?: number | null;
  packUnit?: string | null;
  packSize?: number | null;
}

// Which alternative values the restaurant would accept per flexible attribute.
// Keys mirror the *Flexible flags; a key only matters while its flag is true,
// and an absent key / empty list means "open to any value". Catalog-driven in
// the wizard (values come from the family's variants), stored as-is by the BE.
export interface AcceptableAlternatives {
  brand?: string[];
  origin?: string[];
  state?: string[];
  spec?: string[];
  // The one non-string key — pack formats are compared field-by-field, so
  // storing a rendered label would force the BE to port packagingSummary and
  // its English pluralizer. See utils/packaging-spec.
  packaging?: PackagingSpec[];
}

export interface CreateRestaurantInquiryItem {
  masterProductId: number; // must exist — server 400s on any invalid id in the batch
  // Positive when supplied. Optional since UMS #2289 (2026-09-10): the mobile
  // Sales Lookup basket logs interest before a quantity is known. An older BE
  // 400s on a missing volume — deploy BE first.
  volume?: number;
  volumePeriod?: VolumePeriod; // default PER_ORDER
  currentPrice?: number;
  expectedPrice?: number;
  currency?: CurrencyCode; // default AED
  brandFlexible?: boolean; // default true
  originFlexible?: boolean; // default true
  // Accepted by the 2026-07-07 UMS update; older backends (staging until the
  // deploy) silently ignore them — verified, no 400.
  specFlexible?: boolean;
  stateFlexible?: boolean;
  packagingFlexible?: boolean; // default true — covers itemUnit/netContent/packUnit/packSize, never baseUnit
  // Omitted = open to any (see AcceptableAlternatives). Pre-deploy BEs ignore it.
  acceptableAlternatives?: AcceptableAlternatives;
  notes?: string;
  // When the restaurant's current stock of this item expires (date-only,
  // YYYY-MM-DD) and whether it's already near expiry — urgency signals for
  // the procurement desk. Pre-deploy BEs silently drop both (no whitelist).
  expirationDate?: string;
  isNearExpiry?: boolean;
}

export interface CreateRestaurantInquiryPayload {
  restaurantId: number; // must exist — server 404s if not
  paymentOption: PaymentOption;
  creditDays?: number; // default 0; only meaningful for CREDIT
  notes?: string;
  // Omit in practice: defaults to NEW, and transitions go through
  // PATCH :id/status (state machine + audit); setting it here bypasses both.
  status?: RestaurantInquiryStatus;
  // Omit or [] for a header-only inquiry (items added later via POST :id/items).
  items?: CreateRestaurantInquiryItem[];
}

// POST :id/items — append line items to an existing inquiry. Unlike create,
// items must have at least 1 entry (empty array is a 400).
export interface AddRestaurantInquiryItemsPayload {
  items: CreateRestaurantInquiryItem[];
}

// PATCH :id/items/:itemId — true partial update: send only changed fields;
// omitted fields are left untouched. currentPrice/expectedPrice accept an
// explicit null to CLEAR the stored value (distinct from omitting).
export interface UpdateRestaurantInquiryItemPayload {
  masterProductId?: number; // re-validated server-side if present
  volume?: number; // positive
  volumePeriod?: VolumePeriod;
  currentPrice?: number | null;
  expectedPrice?: number | null;
  currency?: CurrencyCode;
  brandFlexible?: boolean;
  originFlexible?: boolean;
  specFlexible?: boolean;
  stateFlexible?: boolean;
  packagingFlexible?: boolean;
  // Replaces the whole object; explicit null CLEARS it.
  acceptableAlternatives?: AcceptableAlternatives | null;
  notes?: string;
  // Explicit null CLEARS the stored date (same tri-state as the prices).
  expirationDate?: string | null;
  isNearExpiry?: boolean;
}

// PATCH :id/status — the ONLY route that changes inquiry status post-creation
// (terminal-state lock + audit event). No transition-graph validation server-side
// beyond the terminal check; the UI enforces the intended sequence.
export interface UpdateRestaurantInquiryStatusPayload {
  status: RestaurantInquiryStatus;
  reason?: string; // stored on the audit event, not the inquiry
}

// PATCH :id/items/:itemId/status — same idea per line item.
export interface UpdateRestaurantInquiryItemStatusPayload {
  status: RestaurantInquiryItemStatus;
  reason?: string;
}

// GET :id/history — append-only audit feed (createdAt DESC).
export type InquiryEventEntityType = 'inquiry' | 'item' | 'quote';
// Narrow today; QUOTED, FULFILLMENT_DECIDED etc. arrive in later phases —
// treat as an open set and render unknown values generically.
export type InquiryEventType = 'STATUS_CHANGED' | 'ASSIGNED' | (string & Record<never, never>);

export interface RestaurantInquiryEvent {
  id: number;
  restaurantInquiryId: number;
  entityType: InquiryEventEntityType;
  entityId: number; // the item's id for entityType 'item', else the inquiry's
  eventType: InquiryEventType;
  field?: string | null;
  fromValue?: string | null; // always strings, even for non-string fields
  toValue?: string | null;
  reason?: string | null;
  metadata?: unknown;
  actorId?: number | null;
  createdAt: string;
  actor?: AssigneeSummary | null;
}

// POST :id/items/:itemId/quotes — attach a supplier quote to an item. The quote
// targets a real supplier SKU (productId from `products`), NOT a masterProductId;
// the backend derives supplierId from the product. status is always PENDING on
// create. 409 = this product is already quoted on this item.
export interface CreateInquiryQuotePayload {
  productId: number;
  price: number;
  currency?: CurrencyCode; // default AED
  notes?: string;
  // Which rung of the product's packaging ladder `price` was agreed against.
  // Ignored by a pre-update backend, which leaves the quote unit-less and the
  // UI inferring from defaultUnit — see QuotePriceUnit.
  priceUnit?: QuotePriceUnit;
  // true = the BE also upserts the quoted product's (supplier × master)
  // supplier_prices row in the same transaction (UMS "syncSupplierPrice",
  // 2026-09-13). An older BE silently ignores it. 400 when the product has
  // no master link.
  syncSupplierPrice?: boolean;
}

// POST :id/send-order — one click = one order = one supplier. Items are awarded
// (ACCEPTED) quotes of a single supplier; quantity defaults to the item volume
// and is editable. 409 = a selected quote was already ordered.
export interface SendInquiryOrderPayload {
  branchId: number;
  supplierUserId: number;
  items: { quoteId: number; quantity: number }[];
  // Special requests from either party — rendered on the LPO's Additional
  // Comments (after the supplier's payment terms).
  comment?: string;
  // false = the BE produces no order notification queues (email/whatsapp/
  // chat/push) at all; absent behaves as true (pre-checkbox contract), so the
  // modal always sends the checkbox value explicitly.
  sendNotifications?: boolean;
  /** GPO commission for this order. Required at send time — the hourly BE sweep that
   * used to stamp the profile rate after the fact has been retired. */
  commission: {
    type: 'percent' | 'fixed';
    value: number;
  };
}

export interface SendInquiryOrderResult {
  orderId: number;
  code: string;
  supplierId: number;
  supplierName?: string | null;
  itemsCount: number;
  totalPrice: number;
}

export interface SupplierUserSummary {
  id: number;
  name?: string | null;
  email?: string | null;
  // Available once UMS PR #2188 is deployed — shown as the selected SAM's
  // contact details in the Send Order modal.
  phoneNumber?: string | null;
}

export interface InquiryBranchSummary {
  id: number;
  name?: { en?: string | null; ar?: string | null } | null;
  address?: string | null;
  isActive?: boolean;
}

export enum InquiryQuoteStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

// AUTO = created by the auto-quotation pass on item creation (user-mngt-srv
// PR #2186) — those quotes carry creator: null, so "Quoted by" must read the
// source instead. MANUAL is the column default; rows predating the column were
// backfilled MANUAL.
export enum InquiryQuoteSource {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
}

// APP inquiries are opened by the mobile Marketplace on behalf of a restaurant user: each item is a
// price request (a draft order). Ops quote + award as usual — the AWARD confirms the price to the
// restaurant — but never Send Order: the restaurant places the order from the app.
export type InquirySource = 'CMS' | 'APP';

export type GpoRequestStatus = 'pending' | 'confirmed' | 'placed' | 'cancelled' | 'expired';

export interface GpoRequestView {
  orderId: number;
  status: GpoRequestStatus;
  quantity: number;
  unit: string | null;
  indicativeUnitPrice: number;
  confirmedUnitPrice: number | null;
  expiresAt: string | null;
  currency: string;
}

export interface GpoAwardConfirmationMeta {
  orderId: number;
  confirmedUnitPrice: number;
  expiresAt: string;
}

// Which rung of the offered product's packaging ladder a quote's `price` was
// agreed against — PACK = the master's packUnit (carton), ITEM = its itemUnit
// (piece), BASE = its baseUnit (kg). Stored as a LADDER POSITION, not a unit
// word, so re-packaging a master re-renders old quotes correctly instead of
// stranding a literal "carton". Absent on quotes predating the column, where
// the UI infers the rung from defaultUnit for display only.
// See pages/inquiry-processor/utils/price-units.ts for the conversion rules.
export type QuotePriceUnit = 'PACK' | 'ITEM' | 'BASE';

// The packaging ladder of a supplier product, read through its master link.
// NESTED, not flattened: these columns live on master_products, not products —
// `products` carries only the flat free-text defaultUnit. An unlinked product
// serializes masterProduct: null and has no ladder at all.
//
// Optional until every env deploys the UMS pair; while absent the price renders
// as the bare `price + defaultUnit` it always did. Same field semantics as the
// master-products module — only baseUnit is ever guaranteed, so a null
// netContent/packSize means UNKNOWN and blocks the conversions that need it.
export interface ProductUomLadder {
  id?: number;
  baseUnit?: string | null;
  itemUnit?: string | null;
  netContent?: number | null;
  packUnit?: string | null;
  packSize?: number | null;
}

export interface QuoteProductSummary {
  id: number;
  masterProduct?: ProductUomLadder | null;
  nameEn?: string | null;
  nameAr?: string | null;
  brandEn?: string | null;
  // The supplier's own free-text selling unit. Unstructured and unrelated to
  // the ladder above — matched against it only as a display fallback.
  defaultUnit?: string | null;
  // Shipped by UMS (verified against local user-mngt-srv 2026-07-07; staging
  // follows on deploy). NOTE: the field is `specs` (plural) — same naming as
  // QuotationSupplierProduct; the item's masterProduct uses `spec` (singular).
  // While a payload lacks them, the matrix falls back to the supplier-catalog
  // row of the same productId.
  origin?: string | null;
  state?: string | null;
  specs?: string | null;
}

export interface RestaurantInquiryQuote {
  id: number;
  restaurantInquiryItemId: number;
  productId: number;
  supplierId: number; // server-derived from the product
  status: InquiryQuoteStatus;
  // Optional until every env runs the auto-quotation deploy; absent = MANUAL.
  source?: InquiryQuoteSource;
  price: number;
  currency: CurrencyCode;
  // The rung `price` was agreed against. Absent on pre-column quotes and on a
  // pre-update backend; the matrix then infers it from the product's
  // defaultUnit rather than showing a wrong unit.
  priceUnit?: QuotePriceUnit | null;
  notes?: string | null;
  // NEVER POPULATED. restaurant_inquiry_quotes has no metadata column at all —
  // acceptQuote records which quotes lost to an award only on the EVENT row it
  // writes per sibling (metadata.lostToQuoteId), which is what the revert
  // endpoint reads to restore them. Any UI keyed on this field is dead code.
  // Kept so the shape isn't silently re-invented; read the inquiry history
  // instead.
  metadata?: { lostToQuoteId?: number } | null;
  // Set once the awarded quote is sent in an order (CMS "Send Order") — terminal:
  // the same quote can never be ordered again. `order.code` links to
  // /app/orders/details/:code; orderedByUser is the CMS admin ("Sent by").
  orderId?: number | null;
  orderedBy?: number | null;
  order?: { id: number; code: string } | null;
  orderedByUser?: AssigneeSummary | null;
  product?: QuoteProductSummary;
  supplier?: { id: number; name?: string };
  creator?: AssigneeSummary | null;
  updater?: AssigneeSummary | null;
  createdAt?: string;
  updatedAt?: string;
}

// GET /products/:productId/quote-history — every quote ever made for a supplier
// SKU across all inquiries, newest inquiry first then newest quote within it.
// Rows are durable: they survive soft-deletion of their inquiry/item, so don't
// assume the referenced inquiry is still openable.
export interface QuoteHistoryInquiryRef {
  id: number;
  status?: RestaurantInquiryStatus;
  createdAt?: string;
  // When the inquiry was resolved (any terminal status); null while still open.
  fulfilledAt?: string | null;
  restaurant?: RestaurantSummary;
}

export interface QuoteHistoryItemRef {
  id: number;
  status?: RestaurantInquiryItemStatus;
  volume?: number;
  volumePeriod?: VolumePeriod;
  restaurantInquiry?: QuoteHistoryInquiryRef | null;
}

export interface ProductQuoteHistoryEntry {
  id: number;
  restaurantInquiryItemId: number;
  productId: number;
  supplierId: number;
  status: InquiryQuoteStatus;
  source?: InquiryQuoteSource;
  price: number;
  currency: CurrencyCode;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
  supplier?: { id: number; name?: string };
  restaurantInquiryItem?: QuoteHistoryItemRef | null;
}

// Quotation-source discovery (#2146). These sit under /cms/restaurant-inquiries
// but are NOT scoped to an inquiry — they exist to find who/what to quote.
export interface QuotationSupplier {
  id: number; // supplier_profile id
  supplierName: string;
  category?: string | null;
  moq?: string | null;
  delivery?: string | null;
  paymentTerms?: string | null;
  supplierId: number;
  // countryCode ships with user-mngt-srv wt-quotation-suggestions — absent on
  // older BEs, and the inline add-deal affordance hides itself while it is.
  supplier?: { id: number; name?: string; countryCode?: string | null };
}

export interface ExclusiveDealSummary {
  id: number;
  dealPrice?: number | null;
  originalPrice?: number | null;
  cashbackPercent?: number | null;
  productExpiryDate?: string | null;
  // When a CMS user last edited the deal — the staleness signal for the quote
  // modal's Deal Price line.
  lastModifiedAt?: string | null;
  // Older staleness field requested before lastModifiedAt existed; kept as a
  // fallback in case some environments send it.
  updatedAt?: string | null;
  // False for a deactivated/expired deal — the endpoint now returns these
  // too (previously it silently omitted them), so the UI must flag them.
  isActive?: boolean;
}

// The supplier's full public catalog once user-mngt-srv PR #2189 deploys
// (previously deal-backed rows only). Deal-less products carry
// exclusiveDeals: []; when a product has more than one historical deal, the
// most recent (highest id) is listed first in exclusiveDeals.
export interface QuotationSupplierProduct {
  id: number; // the productId to pass to POST .../quotes
  // The packaging ladder, via the master link — see ProductUomLadder.
  masterProduct?: ProductUomLadder | null;
  nameEn?: string | null;
  nameAr?: string | null;
  brandEn?: string | null;
  specs?: string | null;
  defaultPrice?: number | null;
  defaultUnit?: string | null;
  imageUrl?: string | null;
  origin?: string | null;
  // Live on staging (checked 2026-07-07) — used to enrich the match matrix.
  state?: string | null;
  supplierId: number;
  exclusiveDeals?: ExclusiveDealSummary[];
  // Master-product link (spec: master-linked-supplier-products). Set once the
  // product is created-from / linked-to a master; optional until every env
  // serializes it.
  masterProductId?: number | null;
  // Only on suggested-endpoint rows: LINKED = the supplier's master-linked
  // product (auto-selected), NAME = name-scored link candidate.
  matchSource?: 'LINKED' | 'NAME';
}

// GET /statistics — status-keyed counts (every enum key always present) + total.
// Unscoped globally: no restaurant/date filtering available yet.
// newMatches = DISTINCT inquiries (any status except DRAFT — reactivation
// update 2026-07-22) with >=1 matchable item, system-wide — always equals
// `total` of the ?hasNewMatches=true list (same backend query).
// Optional until every env deploys inquiry-new-match-badges-backend.
export type RestaurantInquiryStatistics = Record<RestaurantInquiryStatus, number> & {
  total: number;
  newMatches?: number;
};

// Response shapes, confirmed against the staging GET/POST responses.
export interface RestaurantSummary {
  id: number;
  nameEn?: string;
  nameAr?: string;
}

export interface AssigneeSummary {
  id: number;
  name?: string;
  email?: string;
  phoneNumber?: string | null;
}

// Ancestor chain for the product's catalog category, leaf-first
// (e.g. depth 3 = leaf … depth 0 = root). Empty if unresolved.
export interface CategoryTreeNode {
  id: number;
  name: string;
  depth: number;
}

export interface MasterProductSummary {
  id: number;
  name: string;
  // Optional until every env returns it (user-mngt-srv PR #2183) — drives the
  // one-item-per-family exclusion in the items board's family picker.
  familyId?: number | null;
  brand?: string | null;
  baseUnit?: string | null;
  packUnit?: string | null;
  // Live on staging (checked 2026-07-06) — the request side of the match matrix.
  origin?: string | null;
  state?: string | null;
  spec?: string | null;
  catalogCategoryId?: number | null;
  categoryTree?: CategoryTreeNode[];
  // UOM breakdown + image — serialized by user-mngt-srv PR #2189 (optional
  // until every env deploys it). Same semantics as the master-products module:
  // netContent = "Base quantity", itemUnit = "Base Unit" e.g. piece/bottle, packSize =
  // "Package quantity" (packUnit already above).
  netContent?: number | null;
  itemUnit?: string | null;
  packSize?: number | null;
  imageUrl?: string | null;
}

export interface InquiryItemNewMatch {
  supplierId: number;
  supplierName: string | null;
  productId: number;
  productName: string | null;
  dealId: number;
  dealPrice: number;
}

export interface RestaurantInquiryItem {
  id: number;
  restaurantInquiryId: number;
  masterProductId: number;
  volume: number | null; // null = not captured (nullable since UMS #2289)
  volumePeriod: VolumePeriod;
  currentPrice?: number | null;
  expectedPrice?: number | null;
  currency: CurrencyCode;
  brandFlexible: boolean;
  originFlexible: boolean;
  // Optional until every env returns them. specFlexible live on staging since
  // 2026-07-06; stateFlexible verified on local UMS 2026-07-07 (BE default:
  // true — "open to switch" — for items created without it).
  specFlexible?: boolean;
  stateFlexible?: boolean;
  // Optional until every env returns it (user-mngt-srv, 2026-07-31). BE default: true.
  packagingFlexible?: boolean;
  // Optional until every env returns it (BE deploy 2026-07-10).
  acceptableAlternatives?: AcceptableAlternatives | null;
  status?: RestaurantInquiryItemStatus;
  // Set only on an APP-sourced item: the id of the draft order (GPO request) this item is the
  // price for, and its live state. Optional until every env serializes it.
  gpoOrderId?: number | null;
  gpoRequest?: GpoRequestView | null;
  notes?: string | null;
  // Optional until every env returns them (user-mngt-srv pair of 2026-08-18).
  // Date-only YYYY-MM-DD; isNearExpiry defaults false server-side.
  expirationDate?: string | null;
  isNearExpiry?: boolean;
  masterProduct?: MasterProductSummary;
  quotes?: RestaurantInquiryQuote[];
  // Active exclusive deals that can serve this item: a supplier matches unless
  // they already have a live (non-REJECTED) quote on the item — nothing else
  // gates it, not the item's or the inquiry's status (reactivation update
  // 2026-07-22). Present only when GET :id is called with
  // ?includeNewMatches=true; explicit [] for non-matchable items.
  // Computed at read time — never cache across fetches.
  newMatches?: InquiryItemNewMatch[];
  creator?: AssigneeSummary | null;
  updater?: AssigneeSummary | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RestaurantInquiry {
  id: number;
  restaurantId: number;
  paymentOption: PaymentOption;
  creditDays?: number;
  notes?: string | null;
  status: RestaurantInquiryStatus;
  // CMS = created by an ops user; APP = opened by the mobile Marketplace on the restaurant's
  // behalf. Optional until every env serializes it; absent behaves as CMS.
  source?: InquirySource;
  restaurant?: RestaurantSummary;
  // Audit fields as {id, name} objects (or null). assignee is null until
  // someone transitions the inquiry to PROCESSING (sticky assignment).
  // assignedTo (bare int) survives during the backend transition period.
  assignedTo?: number | null;
  assignee?: AssigneeSummary | null;
  creator?: AssigneeSummary | null;
  updater?: AssigneeSummary | null;
  // Present on GET :id and write responses; the LIST endpoint returns
  // itemsCount instead of the items array.
  items?: RestaurantInquiryItem[];
  itemsCount?: number;
  // Number of this inquiry's matchable ITEMS (not match rows / suppliers) —
  // an item matched by 4 suppliers still counts once. Present only when the
  // LIST is fetched with ?includeNewMatches=true; explicit 0 when nothing
  // matches. Since the reactivation update (2026-07-22) closed inquiries
  // (FULFILLED/UNFULFILLED/CANCELLED) count too — only DRAFT always reports 0.
  // Read-time-computed, never cache.
  newMatchesCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

// nestjs-crud style paginated envelope.
export interface Paginated<T> {
  data: T[];
  page: number;
  total: number;
  pageCount: number;
  count: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export type RestaurantInquiryListResponse = Paginated<RestaurantInquiry>;

export interface GetRestaurantInquiriesParams {
  page?: number;
  limit?: number;
  restaurantId?: number;
  masterProductId?: number;
  status?: RestaurantInquiryStatus;
  paymentOption?: PaymentOption;
  // Attach newMatchesCount to every row. Omitted → response byte-identical to
  // before the param existed, so leave it off everywhere but the Match Maker rail.
  includeNewMatches?: boolean;
  // Only return inquiries with >=1 matchable item (the "New match" filter chip).
  // Composable with includeNewMatches and the other filters.
  hasNewMatches?: boolean;
  // Restrict to CMS- or APP-sourced inquiries. Omitted = both.
  source?: InquirySource;
}

export interface GetRestaurantInquiryParams {
  // Attach newMatches[] to every entry of items[]. Omitted → response
  // identical to before the param existed.
  includeNewMatches?: boolean;
}

// GET /master-products/unmatched-demand — sourcing-opportunity report: master
// products restaurants keep requesting with zero CONFIRMED supplier match.
// PENDING matches still count as unmatched (not yet verified as the same product).
// Contract per docs/plans 2026-07-10 frontend handoff (Phase 1), verified
// against staging 2026-07-13.

// no_quotes = quotesReceivedCount === 0; quoted_no_match = > 0 but no CONFIRMED match.
export enum UnmatchedDemandMatchingStatus {
  NO_QUOTES = 'no_quotes',
  QUOTED_NO_MATCH = 'quoted_no_match',
}

// `all` is the BE default; the param mirrors UnmatchedDemandMatchingStatus otherwise.
export type UnmatchedDemandStatusFilter = 'all' | 'no_quotes' | 'quoted_no_match';

// inquiries = inquiryCount DESC (default); waiting_days = oldest firstRequestedAt
// first; last_requested = newest first. All single-direction, tie-break id ASC.
export type UnmatchedDemandSort = 'inquiries' | 'waiting_days' | 'last_requested';

export interface MasterProductVolumeDemand {
  volume: number; // in the row's baseUnit
  volumePeriod: VolumePeriod;
  count: number; // inquiry items requesting exactly this volume+period pair
  suspect: boolean; // repdigit heuristic (111, 2222, ...) — "worth a second look"
}

// A price manually transcribed by CMS ops after an offline supplier conversation —
// there is no supplier portal/RFQ loop, so UI copy must not imply outreach state.
export interface UnmatchedDemandQuote {
  supplierId: number;
  supplierName: string | null;
  price: number;
  currency: string;
  receivedAt: string;
}

export interface UnmatchedDemandInquiryItem {
  restaurantInquiryId: number;
  itemId: number;
  restaurantId: number;
  restaurantName: string | null;
  qty: number | null; // null = volume not captured on the item (UMS #2289)
  suspect: boolean;
  requestedAt: string;
}

export interface UnmatchedMasterProductDemand {
  masterProductId: number;
  name: string | null;
  brand: string | null;
  origin: string | null;
  state: string | null;
  spec: string | null; // free-text notes — NOT pack size
  packSize: number | null; // usually null in current data
  packUnit: string | null; // e.g. 'GRAMS'; pair with packSize for display
  baseUnit: string; // single value per product (multi-UOM not representable yet)
  // MONTHLY as-is, WEEKLY ×52/12, QUARTERLY ÷3, ANNUAL ÷12; PER_ORDER/ONE_TIME excluded.
  totalPerMonth: number;
  suspect: boolean;
  inquiryCount: number; // distinct inquiries — headline/default sort metric
  itemCount: number; // >= inquiryCount; an inquiry can list the product more than once
  firstRequestedAt: string; // oldest open request — drives the Waiting column
  lastRequestedAt: string;
  matchingStatus: UnmatchedDemandMatchingStatus;
  quotesReceivedCount: number;
  quotes: UnmatchedDemandQuote[]; // sorted newest-first
  inquiryItems: UnmatchedDemandInquiryItem[];
  volumeBreakdown: MasterProductVolumeDemand[];
  owner: null; // always null this iteration — render unassigned state, no interaction
}

// Reflects category/origin filters but never the status filter — powers the
// status tab counts, which must stay stable no matter which tab is active.
export interface UnmatchedDemandSummary {
  totalProducts: number;
  totalOpenInquiries: number;
  noQuotesCount: number;
  quotedNoMatchCount: number;
}

export type UnmatchedMasterProductDemandListResponse = Paginated<UnmatchedMasterProductDemand> & {
  summary: UnmatchedDemandSummary;
};
